import React, { useState, useEffect } from 'react';
import { apiFetch } from './utils/api';

function SettingsEmployeeStatus() {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    try {
      const response = await apiFetch('/api/custom-lists?list_type=employee_status');
      const data = await response.json();
      setStatuses(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching employee statuses:', error);
      setLoading(false);
    }
  };

  const handleAddStatus = async () => {
    if (!newStatus.trim()) {
      alert('Please enter a status name');
      return;
    }

    setSaving(true);
    try {
      const response = await apiFetch('/api/custom-lists', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          list_type: 'employee_status',
          list_value: newStatus.trim()
        })
      });

      if (response.ok) {
        setNewStatus('');
        fetchStatuses();
      } else {
        alert('Error adding status');
      }
    } catch (error) {
      console.error('Error adding status:', error);
      alert('Error adding status');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStatus = async (id, statusName) => {
    if (!window.confirm(`Delete status "${statusName}"?`)) return;

    try {
      await apiFetch(`/api/custom-lists/${id}`, {
        method: 'DELETE'
      });
      fetchStatuses();
    } catch (error) {
      console.error('Error deleting status:', error);
      alert('Error deleting status');
    }
  };

  const handleReorder = async (id, direction) => {
    const currentIndex = statuses.findIndex(s => s.id === id);
    if (currentIndex === -1) return;

    const newOrder = currentIndex + direction;
    if (newOrder < 0 || newOrder >= statuses.length) return;

    try {
      await apiFetch(`/api/custom-lists/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_order: newOrder })
      });
      fetchStatuses();
    } catch (error) {
      console.error('Error reordering status:', error);
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-600">Loading...</div>;
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Header */}
      <div className="border-b border-gray-200 px-6 py-4">
        <h2 className="text-xl font-semibold text-gray-900">Employee Status Types</h2>
        <p className="text-sm text-gray-600 mt-1">
          Manage employee status options (e.g., Active, On Leave, Suspended, Terminated)
        </p>
      </div>

      {/* Body */}
      <div className="p-6">
        {/* Add New Status */}
        <div className="mb-6 flex gap-3">
          <input
            type="text"
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddStatus()}
            placeholder="Enter new status (e.g., Probation)"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            onClick={handleAddStatus}
            disabled={saving || !newStatus.trim()}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition disabled:opacity-50"
          >
            {saving ? 'Adding...' : '+ Add Status'}
          </button>
        </div>

        {/* Status List */}
        {statuses.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No employee statuses defined. Add your first one above.
          </div>
        ) : (
          <div className="space-y-2">
            {statuses.map((status, index) => (
              <div
                key={status.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition"
              >
                <div className="flex items-center space-x-3">
                  <div className="flex flex-col space-y-1">
                    <button
                      onClick={() => handleReorder(status.id, -1)}
                      disabled={index === 0}
                      className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    >
                      ▲
                    </button>
                    <button
                      onClick={() => handleReorder(status.id, 1)}
                      disabled={index === statuses.length - 1}
                      className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    >
                      ▼
                    </button>
                  </div>
                  <span className="font-medium text-gray-900">{status.list_value}</span>
                </div>
                <button
                  onClick={() => handleDeleteStatus(status.id, status.list_value)}
                  className="px-3 py-1 text-red-600 hover:bg-red-50 rounded transition text-sm"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-xl">
        <p className="text-sm text-gray-600">
          💡 <strong>Tip:</strong> Default statuses are Active, On Leave, Suspended, and Terminated.
          Add custom statuses like Probation, Notice Period, or Contractor.
        </p>
      </div>
    </div>
  );
}

export default SettingsEmployeeStatus;
